import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Search, Sparkles, X, ChevronRight, Layers } from 'lucide-react'
import { api } from '@/lib/api'
import { formatPrice } from '@/lib/format'
import { useT } from '@/lib/i18n'
import { useAppStore } from '@/lib/store'
import { usePageTitle } from '@/lib/usePageTitle'
import { haptic } from '@/lib/telegram'
import type { BrandSummary } from '@/shared/types'

export function BrandsPage() {
  const T = useT('brands')
  const lang = useAppStore((s) => s.lang)
  usePageTitle(T.title)

  const [searchQuery, setSearchQuery] = useState('')
  const [selectedLetter, setSelectedLetter] = useState<string>('ALL')

  const { data: brands, isLoading, isError } = useQuery<BrandSummary[]>({
    queryKey: ['brands'],
    queryFn: () => api.get<BrandSummary[]>('/brands'),
    staleTime: 300_000,
  })

  // Collect unique letters across all brands
  const alphabet = useMemo(() => {
    if (!brands) return []
    const letters = new Set<string>()
    for (const b of brands) {
      const firstChar = (b.name.trim()[0] || '').toUpperCase()
      if (/[0-9]/.test(firstChar)) {
        letters.add('#')
      } else if (firstChar) {
        letters.add(firstChar)
      }
    }
    return Array.from(letters).sort((a, b) => {
      if (a === '#') return -1
      if (b === '#') return 1
      return a.localeCompare(b, lang === 'uk' ? 'uk' : 'en')
    })
  }, [brands, lang])

  // Filtered brands by query & letter
  const filteredBrands = useMemo(() => {
    if (!brands) return []
    const query = searchQuery.trim().toLowerCase()

    return brands.filter((brand) => {
      const nameLower = brand.name.toLowerCase()
      const matchesQuery = !query || nameLower.includes(query) || brand.categories.some((c) => c.toLowerCase().includes(query))
      if (!matchesQuery) return false

      if (selectedLetter !== 'ALL') {
        const firstChar = (brand.name.trim()[0] || '').toUpperCase()
        if (selectedLetter === '#') {
          if (!/[0-9]/.test(firstChar)) return false
        } else if (firstChar !== selectedLetter) {
          return false
        }
      }

      return true
    })
  }, [brands, searchQuery, selectedLetter])

  // Group filtered brands by first letter
  const groupedBrands = useMemo(() => {
    const groups: Record<string, BrandSummary[]> = {}
    for (const brand of filteredBrands) {
      const firstChar = (brand.name.trim()[0] || '').toUpperCase()
      const groupKey = /[0-9]/.test(firstChar) ? '#' : firstChar || '?'
      if (!groups[groupKey]) {
        groups[groupKey] = []
      }
      groups[groupKey].push(brand)
    }

    return Object.keys(groups)
      .sort((a, b) => {
        if (a === '#') return -1
        if (b === '#') return 1
        return a.localeCompare(b, lang === 'uk' ? 'uk' : 'en')
      })
      .map((letter) => ({
        letter,
        brands: groups[letter].sort((a, b) => a.name.localeCompare(b.name)),
      }))
  }, [filteredBrands, lang])

  const totalProducts = useMemo(() => {
    if (!brands) return 0
    return brands.reduce((acc, b) => acc + (b.product_count || 0), 0)
  }, [brands])

  return (
    <div className="min-h-screen pb-16 bg-[#1a202c] text-[#f8f3ea]">
      {/* HERO / HEADER SECTION */}
      <section className="relative overflow-hidden border-b border-[#3a4352] bg-gradient-to-b from-[#242c38] via-[#1f2631] to-[#1a202c] px-4 pt-6 pb-6 sm:px-6">
        <div className="mx-auto max-w-5xl">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <div className="inline-flex items-center gap-1.5 rounded-full border border-[#c9a24b]/40 bg-[#c9a24b]/10 px-3 py-1 text-xs font-semibold tracking-wider text-[#c9a24b] uppercase mb-2">
                <Sparkles className="h-3.5 w-3.5" />
                <span>{T.allBrands}</span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#f8f3ea]">
                {T.title}
              </h1>
              <p className="mt-1 text-xs sm:text-sm text-slate-400 max-w-xl">
                {T.subtitle}
              </p>
            </div>

            {/* QUICK STATS */}
            {brands && brands.length > 0 && (
              <div className="flex items-center gap-3">
                <div className="rounded-2xl border border-[#3a4352] bg-[#28303c]/80 px-4 py-2 text-center backdrop-blur-sm shadow-xs">
                  <div className="text-lg font-black text-[#c9a24b]">{brands.length}</div>
                  <div className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">{T.title}</div>
                </div>
                <div className="rounded-2xl border border-[#3a4352] bg-[#28303c]/80 px-4 py-2 text-center backdrop-blur-sm shadow-xs">
                  <div className="text-lg font-black text-[#c9a24b]">{totalProducts}</div>
                  <div className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">{lang === 'en' ? 'Products' : lang === 'ru' ? 'Товаров' : 'Товарів'}</div>
                </div>
              </div>
            )}
          </div>

          {/* SEARCH BAR */}
          <div className="relative mt-5">
            <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={T.searchPlaceholder}
              className="w-full rounded-2xl border border-[#3a4352] bg-[#28303c] py-3 pl-10 pr-10 text-sm text-[#f8f3ea] placeholder-slate-400 focus:border-[#c9a24b] focus:bg-[#2e3745] focus:outline-none focus:ring-2 focus:ring-[#c9a24b]/20 transition shadow-inner"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-slate-400 hover:text-slate-200"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* ALPHABETICAL NAVIGATOR CHIPS */}
          {alphabet.length > 1 && (
            <div className="mt-4 flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-none">
              <button
                type="button"
                onClick={() => {
                  haptic('light')
                  setSelectedLetter('ALL')
                }}
                className={`shrink-0 rounded-xl px-3 py-1.5 text-xs font-bold transition shadow-xs ${
                  selectedLetter === 'ALL'
                    ? 'bg-[#c9a24b] text-[#1a202c] shadow-[#c9a24b]/20 ring-2 ring-[#c9a24b]/40'
                    : 'border border-[#3a4352] bg-[#28303c] text-slate-300 hover:border-slate-500 hover:text-white'
                }`}
              >
                {T.allLetters}
              </button>
              {alphabet.map((letter) => (
                <button
                  key={letter}
                  type="button"
                  onClick={() => {
                    haptic('light')
                    setSelectedLetter(letter)
                  }}
                  className={`shrink-0 h-8 w-8 rounded-xl text-xs font-bold transition flex items-center justify-center shadow-xs ${
                    selectedLetter === letter
                      ? 'bg-[#c9a24b] text-[#1a202c] shadow-[#c9a24b]/20 ring-2 ring-[#c9a24b]/40'
                      : 'border border-[#3a4352] bg-[#28303c] text-slate-300 hover:border-slate-500 hover:text-white'
                  }`}
                >
                  {letter}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CONTENT AREA */}
      <main className="mx-auto max-w-5xl px-4 py-6 sm:px-6">
        {isLoading && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3.5">
            {Array.from({ length: 8 }).map((_, idx) => (
              <div
                key={idx}
                className="animate-pulse rounded-2xl border border-[#3a4352]/60 bg-[#28303c]/50 p-4 h-44 flex flex-col justify-between"
              >
                <div className="h-20 w-full rounded-xl bg-[#323d4c]" />
                <div className="mt-3 h-4 w-3/4 rounded-md bg-[#323d4c]" />
                <div className="mt-1 h-3 w-1/2 rounded-md bg-[#323d4c]/60" />
              </div>
            ))}
          </div>
        )}

        {isError && (
          <div className="rounded-2xl border border-rose-500/30 bg-rose-500/10 p-6 text-center text-rose-300">
            <p className="font-semibold">{T.noBrandsFound}</p>
          </div>
        )}

        {!isLoading && !isError && filteredBrands.length === 0 && (
          <div className="py-16 text-center">
            <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl border border-[#3a4352] bg-[#28303c] text-2xl text-slate-400">
              <Layers className="h-6 w-6 text-[#c9a24b]" />
            </div>
            <p className="text-base font-semibold text-slate-300">{T.noBrandsFound}</p>
            {searchQuery && (
              <button
                type="button"
                onClick={() => {
                  setSearchQuery('')
                  setSelectedLetter('ALL')
                }}
                className="mt-3 inline-flex items-center gap-1.5 rounded-xl border border-[#3a4352] bg-[#28303c] px-4 py-2 text-xs font-semibold text-[#c9a24b] hover:bg-[#323d4c] transition"
              >
                {T.allBrands}
              </button>
            )}
          </div>
        )}

        {!isLoading && !isError && groupedBrands.length > 0 && (
          <div className="space-y-8">
            {groupedBrands.map(({ letter, brands: letterBrands }) => (
              <div key={letter} id={`letter-${letter}`} className="scroll-mt-20">
                <div className="mb-3.5 flex items-center gap-3">
                  <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#c9a24b]/20 border border-[#c9a24b]/40 text-xs font-bold text-[#c9a24b]">
                    {letter}
                  </div>
                  <div className="h-px flex-1 bg-gradient-to-r from-[#3a4352] to-transparent" />
                  <span className="text-[11px] font-semibold text-slate-500">
                    {T.productCount(letterBrands.length)}
                  </span>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3.5">
                  {letterBrands.map((brand) => (
                    <Link
                      key={brand.slug}
                      to="/brands/$brandSlug"
                      params={{ brandSlug: brand.slug }}
                      onClick={() => haptic('light')}
                      className="group flex flex-col justify-between overflow-hidden rounded-2xl border border-[#3a4352] bg-[#242c38] p-3.5 transition-all duration-300 hover:border-[#c9a24b] hover:bg-[#28303c] hover:shadow-lg hover:shadow-[#c9a24b]/5 active:scale-[0.98]"
                    >
                      {/* LOGO CONTAINER */}
                      <div className="h-28 w-full overflow-hidden rounded-xl bg-white flex items-center justify-center p-3 shadow-inner">
                        {brand.logo_url ? (
                          <img
                            src={brand.logo_url}
                            alt={brand.name}
                            className="max-h-full max-w-full object-contain transition duration-500 group-hover:scale-105"
                            loading="lazy"
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center bg-slate-100 p-2 text-center rounded-lg">
                            <span className="font-serif text-sm font-bold tracking-wider text-slate-800 uppercase line-clamp-2">
                              {brand.name}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* BRAND DETAILS */}
                      <div className="mt-3 flex flex-1 flex-col justify-between">
                        <div>
                          <div className="flex items-center justify-between gap-1">
                            <h3 className="text-sm font-bold text-[#f8f3ea] group-hover:text-[#c9a24b] transition-colors line-clamp-1">
                              {brand.name}
                            </h3>
                            <ChevronRight className="h-3.5 w-3.5 text-slate-500 group-hover:text-[#c9a24b] group-hover:translate-x-0.5 transition-all shrink-0" />
                          </div>

                          {brand.categories && brand.categories.length > 0 && (
                            <p className="mt-0.5 text-[11px] text-slate-400 line-clamp-1">
                              {brand.categories.join(' • ')}
                            </p>
                          )}
                        </div>

                        <div className="mt-2.5 flex items-center justify-between pt-2 border-t border-[#3a4352]/60 text-[11px]">
                          <span className="font-medium text-slate-400">
                            {T.productCount(brand.product_count)}
                          </span>
                          {brand.min_price_uah > 0 && (
                            <span className="font-semibold text-[#c9a24b]">
                              {T.priceFrom} {formatPrice(brand.min_price_uah)}
                            </span>
                          )}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  )
}
